import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface TermsOfServiceProps {
  onBack: () => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  useSEOMetaTags({
    title: 'Terms of Service - Orcaseotools | Sofia Host LLC',
    description: 'Terms of Service for Orcaseotools SEO and AEO audit tools. Read our terms and conditions for using our professional website analysis and optimization services.',
    keywords: 'terms of service, terms and conditions, SEO tools terms, website audit terms, Orcaseotools terms, SaaS terms',
    canonicalUrl: 'https://orcaseotools.com/terms-of-service',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service - Orcaseotools",
      "description": "Terms of Service for Orcaseotools SEO and AEO audit tools",
      "url": "https://orcaseotools.com/terms-of-service",
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Orcaseotools",
        "url": "https://orcaseotools.com"
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-6">
          <header>
            <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
            <p className="text-slate-400 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </header>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">1. Agreement to Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Sofia Host LLC ("Company", "we", "us", or "our"), concerning your access to and use of the orcaseotools.com website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site"). You agree that by accessing the Site, you have read, understood, and agreed to be bound by all of these Terms of Service. If you do not agree with all of these Terms of Service, then you are expressly prohibited from using the Site and you must discontinue use immediately.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">2. Description of Service</h2>
            <p className="text-slate-300 leading-relaxed">
              Orcaseotools is a SaaS platform providing SEO (Search Engine Optimization), AEO (Answer Engine Optimization), and GEO (Generative Engine Optimization) tools. Users worldwide can utilize our platform to audit websites, track keywords, analyze competitors, and obtain AI-powered SEO insights.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">3. Account Registration & User Responsibilities</h2>
            <p className="text-slate-300 leading-relaxed">
              You may be required to register with the Site. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable. You are responsible for maintaining the accuracy of your account information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">4. Subscription Plans & Pricing</h2>
            <p className="text-slate-300 leading-relaxed">
              We offer various subscription plans. All prices are in EUR (€). Our current subscription plans are:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li><strong>Weekly Plan:</strong> €19.99/week</li>
              <li><strong>Monthly Plan:</strong> €49.99/month (Most Popular)</li>
              <li><strong>Yearly Plan:</strong> €399.99/year (Equivalent to €33.33/month)</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-2">
              Prices may change over time, but any price changes will not affect your current billing cycle. We will notify you in advance of any price changes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">5. Payment Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              All payments are processed securely via Stripe. By subscribing, you authorize us (via Stripe) to charge the applicable subscription fees to your designated payment method on a recurring basis, depending on your chosen billing cycle (weekly, monthly, or yearly). Subscriptions auto-renew automatically unless canceled before the end of the current billing cycle.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">6. Free Credits & Credit System</h2>
            <p className="text-slate-300 leading-relaxed">
              New users are eligible for a free tier consisting of 10 credits to explore our platform's features. Credits may be consumed based on the usage of various tools (such as auditing websites or AI insights). Unused free credits may expire according to our policies, and we reserve the right to modify or discontinue the free tier at any time.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">7. Cancellation & Subscription Management</h2>
            <p className="text-slate-300 leading-relaxed">
              You can cancel your subscription at any time by logging into your account or contacting us. Your cancellation will take effect at the end of the current paid term. You will retain access to the paid features until your current billing cycle concludes. We do not offer refunds or credits for partial subscription periods, except where required by law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">8. Intellectual Property</h2>
            <p className="text-slate-300 leading-relaxed">
              Unless otherwise indicated, the Site and its original content, features, and functionality are owned by Sofia Host LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">9. User-Generated Content & Data</h2>
            <p className="text-slate-300 leading-relaxed">
              You may input data (such as websites to audit) into our tools. You retain all rights to your data. By using our service, you grant us a license to process and analyze this data solely for the purpose of providing the service to you.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">10. Acceptable Use Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us. Prohibited activities include attempting to bypass security measures, reverse-engineering the platform, scraping data, or using the tools for malicious activities.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">11. Service Availability & Disclaimers</h2>
            <p className="text-slate-300 leading-relaxed">
              The Site is provided on an AS-IS and AS-AVAILABLE basis. You agree that your use of the Site and our services will be at your sole risk. To the fullest extent permitted by law, we disclaim all warranties, express or implied, in connection with the Site and your use thereof, including, without limitation, the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">12. Limitation of Liability</h2>
            <p className="text-slate-300 leading-relaxed">
              In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the Site, even if we have been advised of the possibility of such damages.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">13. Indemnification</h2>
            <p className="text-slate-300 leading-relaxed">
              You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of your use of the Site or breach of these Terms of Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">14. Termination</h2>
            <p className="text-slate-300 leading-relaxed">
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms. If you wish to terminate your account, you may simply discontinue using the Service and cancel your subscription.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">15. Governing Law & Dispute Resolution</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms shall be governed by and defined following the laws of New Mexico, USA. Sofia Host LLC and yourself irrevocably consent that the courts of New Mexico, USA shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">16. Severability</h2>
            <p className="text-slate-300 leading-relaxed">
              If any provision or part of a provision of these Terms of Service is determined to be unlawful, void, or unenforceable, that provision or part of the provision is deemed severable from these Terms of Service and does not affect the validity and enforceability of any remaining provisions.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">17. Entire Agreement</h2>
            <p className="text-slate-300 leading-relaxed">
              These Terms of Service and any policies or operating rules posted by us on the Site or in respect to the Site constitute the entire agreement and understanding between you and us, overriding any prior or contemporaneous agreements, communications, and proposals.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">18. Changes to Terms</h2>
            <p className="text-slate-300 leading-relaxed">
              We reserve the right, in our sole discretion, to make changes or modifications to these Terms of Service at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms of Service, and you waive any right to receive specific notice of each such change.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">19. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed">
              In order to resolve a complaint regarding the Site or to receive further information regarding use of the Site, please contact us at:
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
              <p className="text-slate-300"><strong>Company:</strong> Sofia Host LLC</p>
              <p className="text-slate-300"><strong>Address:</strong> 1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</p>
              <p className="text-slate-300"><strong>Entity ID:</strong> 4424925</p>
              <p className="text-slate-300"><strong>Jurisdiction:</strong> New Mexico, USA</p>
              <p className="text-slate-300"><strong>Email:</strong> contact@orcaseotools.com</p>
              <p className="text-slate-300"><strong>Phone:</strong> +1 (606) 557-7288</p>
              <p className="text-slate-300"><strong>Website:</strong> orcaseotools.com</p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Thank you for reviewing our Terms of Service. By using <strong>Orcaseotools</strong>, you agree to abide by these terms and conditions.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};
